/**
 * Upgrade Modal
 *
 * Shown when a trial/hibernating user hits a free-generation gate
 * (FreeGenCounter button, JobCard hover overlay, exhausted points).
 * Uses the same StripeCheckoutModal flow under the hood by delegating
 * "Upgrade Now" back to the parent's upgrade callback.
 */
import { useRef } from 'react';
import { X, Sparkles, Zap } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';

export type UpgradeModalReason = 'exhausted' | 'enable_job' | 'regen_section' | 'general';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  reason?: UpgradeModalReason;
  freeGenRemaining?: number;
}

const REASON_COPY: Record<UpgradeModalReason, { title: string; body: string }> = {
  exhausted: {
    title: "You're out of free generations",
    body: "Free generations refill on the 1st of each month. Upgrade to Active for unlimited customized resumes, cover letters, and section regeneration.",
  },
  enable_job: {
    title: 'Generate unlimited customized resumes',
    body: 'Upgrade to Active for unlimited customized resumes, cover letters, watermark-free output, and per-section regeneration.',
  },
  regen_section: {
    title: 'Section regeneration is an Active feature',
    body: 'Active subscribers can regenerate any individual section as many times as they want. Upgrade to unlock per-section regeneration.',
  },
  general: {
    title: 'Upgrade to Active',
    body: 'Get unlimited customized resumes, cover letters, watermark-free output, and per-section regeneration.',
  },
};

export function UpgradeModal({ isOpen, onClose, onUpgrade, reason = 'general', freeGenRemaining }: UpgradeModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useEscapeClose(onClose, isOpen);
  useFocusTrap(modalRef, isOpen);

  if (!isOpen) return null;

  const content = REASON_COPY[reason];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label={content.title}>
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            {content.title}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-gray-700 mb-4">{content.body}</p>

          {freeGenRemaining !== undefined && reason !== 'exhausted' && (
            <div className="mb-4 p-3 bg-gray-50 rounded-md flex items-center gap-2 text-sm">
              <Zap className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">
                You have <strong className="tabular-nums">{freeGenRemaining}</strong> free generation{freeGenRemaining === 1 ? '' : 's'} left this month.
              </span>
            </div>
          )}

          <ul className="text-sm text-gray-700 space-y-1.5 mb-6">
            <li className="flex items-start gap-2"><span className="text-green-600">&#10003;</span> Unlimited customized resumes &amp; cover letters</li>
            <li className="flex items-start gap-2"><span className="text-green-600">&#10003;</span> Watermark-free output</li>
            <li className="flex items-start gap-2"><span className="text-green-600">&#10003;</span> Per-section regeneration</li>
            <li className="flex items-start gap-2"><span className="text-green-600">&#10003;</span> Job retention up to 9999 days</li>
          </ul>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm"
            >
              Maybe Later
            </button>
            <button
              onClick={() => { onClose(); onUpgrade(); }}
              className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-lg text-sm font-semibold shadow-sm"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
